import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { authHeaders } from './restaurantApi';

export const PLATFORM_FEE_MODE_SUBSCRIPTION = 'SUBSCRIPTION';
export const PLATFORM_FEE_MODE_PER_ORDER_PERCENT = 'PER_ORDER_PERCENT';
export const PLATFORM_SUBSCRIPTION_CADENCES = Object.freeze(['ONE_TIME', 'MONTHLY']);

export const DEFAULT_TAX_FORM = Object.freeze({
  gstPercent: '',
  cgstPercent: '',
  sgstPercent: '',
  deliveryFee: '',
  convienceFee: '',
  platformFeeMode: PLATFORM_FEE_MODE_PER_ORDER_PERCENT,
  platformSubscriptionCadence: '',
  platformSubscriptionAmount: '',
  platformFeePercent: '',
});

export const SUGGESTED_TAX_DEFAULTS = Object.freeze({
  gstPercent: '5.00',
  cgstPercent: '2.50',
  sgstPercent: '2.50',
  platformFeeMode: PLATFORM_FEE_MODE_PER_ORDER_PERCENT,
});

export function emptyTaxForm() {
  return { ...DEFAULT_TAX_FORM };
}

export function newTaxForm() {
  return {
    ...emptyTaxForm(),
    ...SUGGESTED_TAX_DEFAULTS,
  };
}

export function taxFormForEdit(tax) {
  if (tax) return taxRecordToForm(tax);
  return newTaxForm();
}

function optionalDecimal(value) {
  if (value === '' || value == null) return undefined;
  const num = Number(value);
  if (!Number.isFinite(num)) return undefined;
  return num;
}

function requiredPercent(value, fieldName) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    throw new Error(`${fieldName} is required`);
  }
  return num;
}

export function taxRecordToForm(tax) {
  if (!tax) return emptyTaxForm();
  return {
    taxId: tax.taxId ?? tax.Tax_Id ?? null,
    gstPercent: tax.gstPercent != null ? String(tax.gstPercent) : '',
    cgstPercent: tax.cgstPercent != null ? String(tax.cgstPercent) : '',
    sgstPercent: tax.sgstPercent != null ? String(tax.sgstPercent) : '',
    deliveryFee: tax.deliveryFee != null ? String(tax.deliveryFee) : '',
    convienceFee: tax.convienceFee != null ? String(tax.convienceFee) : '',
    platformFeeMode:
      tax.platformFeeMode ||
      tax.Platform_Fee_Mode ||
      PLATFORM_FEE_MODE_PER_ORDER_PERCENT,
    platformSubscriptionCadence:
      tax.platformSubscriptionCadence != null
        ? String(tax.platformSubscriptionCadence)
        : '',
    platformSubscriptionAmount:
      tax.platformSubscriptionAmount != null
        ? String(tax.platformSubscriptionAmount)
        : '',
    platformFeePercent:
      tax.platformFeePercent != null ? String(tax.platformFeePercent) : '',
  };
}

export function buildTaxPayload(form, { restaurantId, storeId, includePlatformCommercial = false } = {}) {
  const payload = {
    gstPercent: requiredPercent(form.gstPercent, 'GST %'),
    cgstPercent: requiredPercent(form.cgstPercent, 'CGST %'),
    sgstPercent: requiredPercent(form.sgstPercent, 'SGST %'),
  };

  const deliveryFee = optionalDecimal(form.deliveryFee);
  const convienceFee = optionalDecimal(form.convienceFee);

  if (deliveryFee !== undefined) payload.deliveryFee = deliveryFee;
  if (convienceFee !== undefined) payload.convienceFee = convienceFee;

  if (includePlatformCommercial) {
    const mode = String(form.platformFeeMode || PLATFORM_FEE_MODE_PER_ORDER_PERCENT).toUpperCase();
    payload.platformFeeMode = mode;
    if (mode === PLATFORM_FEE_MODE_SUBSCRIPTION) {
      payload.platformSubscriptionCadence = String(
        form.platformSubscriptionCadence || '',
      ).toUpperCase();
      const amount = optionalDecimal(form.platformSubscriptionAmount);
      if (amount !== undefined) payload.platformSubscriptionAmount = amount;
    } else {
      const percent = optionalDecimal(form.platformFeePercent);
      if (percent !== undefined) payload.platformFeePercent = percent;
    }
  }

  if (restaurantId != null) payload.restaurantId = Number(restaurantId);
  if (storeId != null) payload.storeId = Number(storeId);

  return payload;
}

export function validateTaxForm(form, { includePlatformCommercial = false } = {}) {
  const requiredFields = [
    ['gstPercent', 'GST %'],
    ['cgstPercent', 'CGST %'],
    ['sgstPercent', 'SGST %'],
  ];
  for (const [key, label] of requiredFields) {
    const raw = form[key];
    if (raw === '' || raw == null) {
      return `${label} is required.`;
    }
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0 || num > 100) {
      return `${label} must be between 0 and 100.`;
    }
  }

  const feeFields = [
    ['deliveryFee', 'Delivery fee'],
    ['convienceFee', 'Convenience fee'],
  ];
  for (const [key, label] of feeFields) {
    const raw = form[key];
    if (raw === '' || raw == null) continue;
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) {
      return `${label} must be zero or greater.`;
    }
  }

  if (includePlatformCommercial) {
    const mode = String(form.platformFeeMode || '').toUpperCase();
    if (mode !== PLATFORM_FEE_MODE_SUBSCRIPTION && mode !== PLATFORM_FEE_MODE_PER_ORDER_PERCENT) {
      return 'Select a platform fee mode.';
    }
    if (mode === PLATFORM_FEE_MODE_SUBSCRIPTION) {
      const cadence = String(form.platformSubscriptionCadence || '').toUpperCase();
      if (!PLATFORM_SUBSCRIPTION_CADENCES.includes(cadence)) {
        return 'Subscription cadence must be ONE_TIME or MONTHLY.';
      }
      const amount = Number(form.platformSubscriptionAmount);
      if (!Number.isFinite(amount) || amount < 0) {
        return 'Subscription amount must be zero or greater.';
      }
    } else {
      const raw = form.platformFeePercent;
      if (raw !== '' && raw != null) {
        const percent = Number(raw);
        if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
          return 'Platform fee percent must be between 0 and 100.';
        }
      }
    }
  }

  return null;
}

export function formatPlatformCommercialSummary(form) {
  if (!form) return null;
  const mode = String(form.platformFeeMode || PLATFORM_FEE_MODE_PER_ORDER_PERCENT).toUpperCase();
  if (mode === PLATFORM_FEE_MODE_SUBSCRIPTION) {
    const cadence = form.platformSubscriptionCadence || '—';
    const amount =
      form.platformSubscriptionAmount !== '' && form.platformSubscriptionAmount != null
        ? `₹${form.platformSubscriptionAmount}`
        : '—';
    return { modeLabel: 'Subscription', detail: `${amount} / ${cadence}` };
  }
  const percent =
    form.platformFeePercent !== '' && form.platformFeePercent != null
      ? `${form.platformFeePercent}%`
      : '—';
  return { modeLabel: 'Per-order percent', detail: percent };
}

export async function fetchCompanyTaxes({ restaurantId, storeId } = {}) {
  const params = {};
  if (restaurantId != null) params.restaurantId = restaurantId;
  if (storeId != null) params.storeId = storeId;

  const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.companyTaxes, {
    headers: authHeaders(),
    params,
  });
  return response.data?.taxes ?? [];
}

export async function createCompanyTax(payload) {
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.companyTaxes,
    payload,
    { headers: authHeaders() },
  );
  return response.data?.tax ?? response.data;
}

export async function updateCompanyTax(taxId, payload) {
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.companyTaxById(taxId),
    payload,
    { headers: authHeaders() },
  );
  return response.data?.tax ?? response.data;
}
